var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { ProjectType, StudyStatus } from "healthex-common/study.dtos";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { NamedRoutes } from "./NamedRoutes";
import { SimpleDialogModel } from "./SimpleDialogModel";
import { getStudyStatusDescription } from "./StudyStatusHelpers";
import { filterFalse } from "healthex-common/utils";
import { UserType } from "healthex-common/user.dtos";
const maxUsersToShow = 5;
export class StudyManagerViewModel {
    constructor(container) {
        this.shareDialogModel = new SimpleDialogModel();
        this.teamMembersDialog = new SimpleDialogModel();
        this.confirmDeleteDialog = new SimpleDialogModel();
        this.confirmCompleteDialog = new SimpleDialogModel();
        this.confirmWithdrawDialog = new SimpleDialogModel();
        this.pauseStudyDialog = new SimpleDialogModel();
        this.pauseReason = null;
        this.isSubmittingPauseChange = false;
        this.studiesUIManager = container.studiesUIManager;
        this.routingUIManager = container.routingUIManager;
        this.studyManager = container.studyManager;
        this.bannerUIManager = container.bannerUIManager;
        this.authManager = container.authManager;
    }
    get study() {
        return this.studiesUIManager.studyToManage;
    }
    get onBack() {
        return () => this.routingUIManager.navigateTo(NamedRoutes.RESEARCH_STUDIES);
    }
    get currentTab() {
        return this.studiesUIManager.currentStudyManagerTab;
    }
    setCurrentTab(tab) {
        this.studiesUIManager.setCurrentStudyManagerTab(tab);
    }
    get isPDDEGeneralDataUse() {
        if (this.study === NOT_LOADED || !this.study)
            return false;
        return (this.study.addedByOrganization.type === UserType.EXTERNAL_RESEARCHER && this.study.projectType === ProjectType.GENERAL_DATA_USE);
    }
    get tabsToShow() {
        if (this.study === NOT_LOADED || !this.study)
            return [];
        if (this.study.status === StudyStatus.DRAFT) {
            return [];
        }
        else {
            return filterFalse([
                { value: "PATIENT_MANAGEMENT", label: "Patient management", showBadge: false },
                this.studiesUIManager.isStudyUsingIdentifiedData && {
                    value: "PATIENT_EXPERIENCE",
                    label: "Patient experience",
                    showBadge: false,
                },
                !this.isPDDEGeneralDataUse && { value: "ANALYTICS", label: "Analytics", showBadge: false },
                { value: "DETAILS", label: "Project details", showBadge: false },
            ]);
        }
    }
    get studyStatusDescription() {
        if (this.study === NOT_LOADED || !this.study)
            return undefined;
        return getStudyStatusDescription(this.study);
    }
    get showManageStudyMenu() {
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return this.showCompleteStudyAction || this.showWithdrawStudyAction || this.showPauseOrResumeAction;
    }
    get studyUser() {
        var _a;
        if (this.study === NOT_LOADED || this.study === null)
            return null;
        return (_a = this.study.users.find((user) => user.user.id === this.authManager.id)) !== null && _a !== void 0 ? _a : null;
    }
    get isStudyUser() {
        return !!this.studyUser;
    }
    get pauseStudyButtonEnabled() {
        return !!this.pauseReason;
    }
    get showPauseOrResumeAction() {
        if (!this.isStudyUser)
            return false;
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return this.study.status === StudyStatus.ACTIVE;
    }
    get isStudyPaused() {
        var _a;
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return (_a = this.study.paused) !== null && _a !== void 0 ? _a : false;
    }
    get pauseOrResumeActionText() {
        return this.isStudyPaused ? "Resume project" : "Pause project";
    }
    get showUnpublishedChangesBanner() {
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return !this.isProjectCompletedOrWithdrawn && this.study.hasDraftVersion;
    }
    get showCompleteStudyAction() {
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return this.study.status === StudyStatus.ACTIVE;
    }
    get showWithdrawStudyAction() {
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return ![StudyStatus.DRAFT, StudyStatus.COMPLETED, StudyStatus.WITHDRAWN].includes(this.study.status);
    }
    get isProjectCompletedOrWithdrawn() {
        if (this.study === NOT_LOADED || this.study === null)
            return false;
        return [StudyStatus.COMPLETED, StudyStatus.WITHDRAWN].includes(this.study.status);
    }
    get showAddTeamMembersAsView() {
        return this.isProjectCompletedOrWithdrawn;
    }
    get teamUsersToShow() {
        if (this.study === NOT_LOADED || this.study === null)
            return [];
        const comparator = (a, b) => { var _a, _b; return ((_a = a.user.firstName) !== null && _a !== void 0 ? _a : "ZZZ").localeCompare((_b = b.user.firstName) !== null && _b !== void 0 ? _b : "ZZZ"); };
        if (this.study.users.length <= maxUsersToShow) {
            return this.study.users.sort(comparator);
        }
        else {
            return this.study.users.sort(comparator).slice(0, maxUsersToShow - 1);
        }
    }
    get totalTeamUsers() {
        if (this.study === NOT_LOADED || this.study === null)
            return 0;
        return this.study.users.length;
    }
    async deleteStudy() {
        if (this.study === NOT_LOADED || !this.study)
            return;
        const confirmed = await this.confirmDeleteDialog.showAndWait();
        if (confirmed) {
            await this.studyManager.deleteStudyDraft(this.study);
            this.routingUIManager.navigateTo(NamedRoutes.RESEARCH_STUDIES);
        }
    }
    async pauseOrResumeStudy() {
        if (!this.isStudyPaused) {
            const confirmed = await this.pauseStudyDialog.showAndWait();
            if (!confirmed || this.pauseReason === null) {
                this.pauseReason = null;
                return;
            }
            await this.doPauseOrResumeStudyUpdate();
        }
        else {
            await this.doPauseOrResumeStudyUpdate();
        }
    }
    async doPauseOrResumeStudyUpdate() {
        if (this.study === NOT_LOADED || !this.study)
            return;
        this.isSubmittingPauseChange = true;
        const previousIsStudyPaused = this.isStudyPaused;
        try {
            if (this.isStudyPaused) {
                await this.studyManager.updateStudy(this.study, { paused: false, pauseReason: null });
            }
            else {
                await this.studyManager.updateStudy(this.study, { paused: true, pauseReason: this.pauseReason });
            }
            await this.studiesUIManager.refreshStudyToManage();
            if (previousIsStudyPaused !== this.isStudyPaused) {
                if (this.isStudyPaused) {
                    this.bannerUIManager.displayBanner({ text: "Project successfully paused" });
                }
                else {
                    this.bannerUIManager.displayBanner({ text: "Project successfully resumed" });
                }
            }
        }
        finally {
            this.pauseReason = null;
            this.isSubmittingPauseChange = false;
        }
    }
    updatePauseReason(pauseReason) {
        this.pauseReason = pauseReason.length ? pauseReason : null;
    }
    async editProject() {
        await this.studiesUIManager.editProject();
    }
    async completeStudy() {
        if (this.study === NOT_LOADED || !this.study)
            return;
        const confirmed = await this.confirmCompleteDialog.showAndWait();
        if (confirmed) {
            await this.studyManager.completeStudy(this.study);
            await this.studiesUIManager.refreshStudyToManage();
            this.bannerUIManager.displayBanner({ text: "Project successfully completed" });
        }
    }
    async withdrawStudy() {
        if (this.study === NOT_LOADED || !this.study)
            return;
        const confirmed = await this.confirmWithdrawDialog.showAndWait();
        if (confirmed) {
            await this.studyManager.withdrawStudy(this.study);
            await this.studiesUIManager.refreshStudyToManage();
            this.bannerUIManager.displayBanner({ text: "Project successfully withdrawn" });
        }
    }
    showShareDialog() {
        this.shareDialogModel.show();
    }
    showTeamMembersDialog() {
        void (async () => {
            await this.teamMembersDialog.showAndWait();
            await this.studiesUIManager.refreshStudyToManage();
        })();
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], StudyManagerViewModel.prototype, "pauseReason", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], StudyManagerViewModel.prototype, "isSubmittingPauseChange", void 0);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "study", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "onBack", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "currentTab", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "isPDDEGeneralDataUse", null);
__decorate([
    computed,
    __metadata("design:type", Array),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "tabsToShow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "studyStatusDescription", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "showManageStudyMenu", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "studyUser", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "isStudyUser", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "pauseStudyButtonEnabled", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "showPauseOrResumeAction", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "isStudyPaused", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "pauseOrResumeActionText", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "showUnpublishedChangesBanner", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "showCompleteStudyAction", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "showWithdrawStudyAction", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "isProjectCompletedOrWithdrawn", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "showAddTeamMembersAsView", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "teamUsersToShow", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], StudyManagerViewModel.prototype, "totalTeamUsers", null);
